import { ApiService } from "@/services/api/ApiService";
import {
  AgentAccessDiagnosis,
  AgentAccessGrant,
  AgentAccessGrantWithToken,
  AgentAccessPassword,
  AgentUser,
  CreateGrantData,
  SetAccessPasswordData,
  UpdateGrantData,
} from "@/types/agent/agent-access";

export class AgentAccessService {
  // ---- Access Grants (tokenized links) ----

  static async listGrants(agentId: string): Promise<AgentAccessGrant[]> {
    const response = await ApiService.invoke<{ grants: AgentAccessGrant[] }>({
      resource: "genie-access",
      action: "list",
      id: agentId,
    });
    return response.grants;
  }

  static async listTeamAccess(agentId: string): Promise<AgentUser[]> {
    const response = await ApiService.invoke<{ access: AgentUser[] }>({
      resource: "genie-access",
      action: "list-team-access",
      id: agentId,
    });
    return response.access;
  }

  static async createGrant(
    agentId: string,
    data: CreateGrantData
  ): Promise<AgentAccessGrantWithToken> {
    const response = await ApiService.invoke<{ grant: AgentAccessGrantWithToken }>({
      resource: "genie-access",
      action: "create",
      id: agentId,
      data,
    });
    return response.grant;
  }

  static async updateGrant(
    grantId: number,
    data: UpdateGrantData
  ): Promise<AgentAccessGrant> {
    const response = await ApiService.invoke<{ grant: AgentAccessGrant }>({
      resource: "genie-access",
      action: "update",
      id: String(grantId),
      data,
    });
    return response.grant;
  }

  static async deleteGrant(grantId: number): Promise<void> {
    await ApiService.invoke({
      resource: "genie-access",
      action: "delete",
      id: String(grantId),
    });
  }

  // ---- Access Password ----

  static async getPassword(agentId: string): Promise<AgentAccessPassword> {
    const response = await ApiService.invoke<{ password: AgentAccessPassword }>({
      resource: "genie-access",
      action: "password",
      id: agentId,
    });
    return response.password;
  }

  static async setPassword(agentId: string, data: SetAccessPasswordData): Promise<AgentAccessPassword> {
    const response = await ApiService.invoke<{ password: AgentAccessPassword }>({
      resource: "genie-access",
      action: "set-password",
      id: agentId,
      data,
    });
    return response.password;
  }

  static async removePassword(agentId: string): Promise<void> {
    await ApiService.invoke({
      resource: "genie-access",
      action: "remove-password",
      id: agentId,
    });
  }

  // ---- Team Access (agent_users) ----

  static async shareWithTeam(
    teamId: number,
    agentId: string,
    expiresAt?: string
  ): Promise<{ granted: number }> {
    const response = await ApiService.invoke<{ granted: number }>({
      resource: "teams",
      action: "share-genie",
      id: String(teamId),
      data: { agentId, expires_at: expiresAt },
    });
    return response;
  }

  static async grantMemberAccess(
    agentId: string,
    userId: string,
    expiresAt?: string
  ): Promise<AgentUser> {
    const response = await ApiService.invoke<{ agentUser: AgentUser }>({
      resource: "teams",
      action: "grant-genie-access",
      data: { agentId, userId, expires_at: expiresAt },
    });
    return response.agentUser;
  }

  static async diagnose(agentId: string, userId: string): Promise<AgentAccessDiagnosis> {
    return ApiService.invoke<AgentAccessDiagnosis>({
      resource: "genie-access",
      action: "diagnose",
      id: agentId,
      data: { userId },
    });
  }

  static async removeMemberAccess(agentId: string, userId: string): Promise<void> {
    await ApiService.invoke({
      resource: "teams",
      action: "remove-genie-access",
      data: { agentId, userId },
    });
  }
}
